import React from 'react';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { useBreakpoint } from '@one-thd/sui-atomic-components';
import { windowExists } from '../common/utils/AccountUtils';

/**
 * Custom hook for responsive device detection
 * @returns {Object} Object containing device type booleans
 * @returns {boolean} isMobile - True if viewport is mobile size (< 640px)
 * @returns {boolean} isTablet - True if device channel is tablet
 * @returns {boolean} isDesktop - True if viewport is desktop size (>= 1024px, excluding exactly 1024px)
 * @returns {boolean} isNativeApp - True if running in native app context (placeholder)
 */
export const useResponsive = () => {
  const { channel, isConsumerApp } = React.useContext(ExperienceContext);
  const [windowWidth, setWindowWidth] = React.useState(null);

  // Call hooks at the top level to avoid conditional hook calls
  const smBreakpoint = useBreakpoint('sm');
  const lgBreakpoint = useBreakpoint('lg');

  React.useEffect(() => {
    // Set initial window width and listen for resize
    const handleResize = () => {
      if (windowExists()) {
        setWindowWidth(window.innerWidth);
      }
    };
    if (windowExists()) {
      setWindowWidth(window.innerWidth);
      window.addEventListener('resize', handleResize);
    }
    return () => {
      if (windowExists()) {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, []);

  const isNativeApp = isConsumerApp;
  const isMobile = smBreakpoint.lesser || channel === 'mobile' || isNativeApp;
  const isTablet = channel === 'tablet' || (smBreakpoint.greater && lgBreakpoint.lesser);
  const isDesktop = lgBreakpoint.greater
    && windowExists()
    && windowWidth !== 1024
    && windowWidth !== null
    && !isTablet;

  return React.useMemo(
    () => ({
      isMobile,
      isTablet,
      isDesktop,
      isNativeApp,
    }),
    [isMobile, isTablet, isDesktop, isNativeApp]
  );
};
